import type { GameSessionInternal, UserSessionListItem } from '@repo/types/sessions/session-types';
import { transformApiSession } from '@repo/types/sessions/session-types';
import { getUserSession, listSessionMetadata, listUserSessions } from '@repo/wemod-api-client/sessions-api-client';
import { useCallback, useEffect, useRef, useState } from 'react';
interface UseSessionDataOptions {
  titleId?: number;
  autoFetch?: boolean;
}
export const useSessionData = (options: UseSessionDataOptions = {}) => {
  const {
    titleId,
    autoFetch = true
  } = options;
  const [sessions, setSessions] = useState<GameSessionInternal[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const lastFetchedTitleIdRef = useRef<number | undefined>(undefined);
  const metadataMapRef = useRef<Map<string, UserSessionListItem>>(new Map());
  const fetchMetadata = useCallback(async (id: number) => {
    try {
      const response = await listSessionMetadata(id);
      metadataMapRef.current = new Map(response.data.map(item => [item.sessionId, item]));
    } catch {
      metadataMapRef.current = new Map();
    }
  }, []);
  const applyMetadata = useCallback((sessions_0: GameSessionInternal[]): GameSessionInternal[] => {
    const map = metadataMapRef.current;
    return sessions_0.map(s => {
      const meta = map.get(s.uuid);
      if (meta) {
        return {
          ...s,
          isShared: meta.isShared,
          hasPositions: meta.positionCount > 0,
          hasFirestoreData: true
        };
      }
      return {
        ...s,
        hasPositions: false,
        hasFirestoreData: false
      };
    });
  }, []);
  const fetchSessions = useCallback(async (pageNum: number) => {
    if (!titleId) return;
    const isFirstPage = pageNum === 1;
    if (isFirstPage) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);
    try {
      // Metadata endpoint is unpaginated — returns all sessions for the title at once.
      // Only fetched on page 1; subsequent pages reuse the cached metadataMapRef.
      const [response_0] = await Promise.all([listUserSessions(pageNum, 15, {
        titleId
      }), isFirstPage ? fetchMetadata(titleId) : Promise.resolve(undefined)]);
      const transformedSessions = response_0.data.map(apiSession => transformApiSession(apiSession));
      const enrichedSessions = applyMetadata(transformedSessions);
      setSessions(prev => {
        if (pageNum === 1) {
          return enrichedSessions;
        }
        const existingIds = new Set(prev.map(s_0 => s_0.uuid));
        const newSessions = enrichedSessions.filter(s_1 => !existingIds.has(s_1.uuid));
        return [...prev, ...newSessions];
      });
      setHasMore(response_0.meta.current_page < response_0.meta.last_page);
      setTotalCount(response_0.meta.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch sessions');
    } finally {
      if (isFirstPage) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  }, [titleId, fetchMetadata, applyMetadata]);
  const fetchSessionDetails = useCallback(async (uuid: string, startTime: string, endTime: string): Promise<GameSessionInternal | null> => {
    if (!titleId || !uuid || !startTime || !endTime) {
      return null;
    }
    try {
      const sessionData = await getUserSession(uuid, startTime, endTime, titleId.toString());
      const isShared = sessionData.isShared;
      const apiSession_0 = sessions.find(s_2 => s_2.uuid === uuid);
      if (!apiSession_0) {
        return transformApiSession({
          uuid,
          gameId: 0,
          titleId,
          titleSlug: null,
          titleName: null,
          name: null,
          status: 'completed',
          startedAt: startTime,
          completedAt: endTime,
          createdAt: startTime,
          updatedAt: endTime
        }, {
          positions: sessionData.positions,
          events: sessionData.events
        }, {
          isShared
        });
      }
      return transformApiSession({
        uuid: apiSession_0.uuid,
        gameId: 0,
        titleId: apiSession_0.titleId,
        titleSlug: null,
        titleName: apiSession_0.titleName,
        name: apiSession_0.name,
        status: apiSession_0.status,
        startedAt: apiSession_0.startedAt,
        completedAt: apiSession_0.completedAt,
        createdAt: apiSession_0.startedAt,
        updatedAt: apiSession_0.startedAt
      }, {
        positions: sessionData.positions,
        events: sessionData.events
      }, {
        isShared
      });
    } catch (_err) {
      return null;
    }
  }, [titleId, sessions]);
  const loadMore = useCallback(() => {
    if (!loading && !loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      void fetchSessions(nextPage);
    }
  }, [loading, loadingMore, hasMore, page, fetchSessions]);
  const updateSessionSharing = useCallback((sessionUuid: string, isShared_0: boolean) => {
    setSessions(prev_0 => prev_0.map(s_3 => s_3.uuid === sessionUuid ? {
      ...s_3,
      isShared: isShared_0
    } : s_3));
  }, []);
  const refetch = useCallback(() => {
    setPage(1);
    setSessions([]);
    void fetchSessions(1);
  }, [fetchSessions]);
  useEffect(() => {
    if (!autoFetch || !titleId) return;
    const titleChanged = lastFetchedTitleIdRef.current !== titleId;
    if (!titleChanged) return;
    lastFetchedTitleIdRef.current = titleId;
    setPage(1);
    setSessions([]);
    void fetchSessions(1);
  }, [autoFetch, titleId, fetchSessions]);
  return {
    sessions,
    loading,
    loadingMore,
    error,
    hasMore,
    totalCount,
    loadMore,
    fetchSessionDetails,
    updateSessionSharing,
    refetch
  };
};